import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from "react";
import {
  VStack,
  HStack,
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Select,
  NumberInput,
  NumberInputField,
  Text,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  IconButton,
  useToast,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Tooltip,
  Switch,
  ButtonGroup,
  Collapse,
  useDisclosure,
  useBreakpointValue,
  Divider,
  Progress,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  AlertDescription,
  AlertTitle,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import * as XLSX from 'xlsx';
import {
  FiSave,
  FiEdit2,
  FiRefreshCw,
  FiDownload,
  FiUpload,
  FiColumns,
  FiMaximize2,
  FiMinimize2,
  FiArrowRight,
  FiInfo,
  FiPlus,
  FiMinus,
  FiChevronRight,
  FiChevronDown,
  FiClock,
  FiDollarSign,
  FiTrendingUp,
  FiActivity,
  FiSearch,
} from "react-icons/fi";

// CPI Calculation Function (moved outside as regular function)
const calculateCPI = (
  task,
  currentQuantity = null,
  currentProductivity = null
) => {
  // Use current values if provided, otherwise use task values
  const quantity = currentQuantity !== null ? currentQuantity : task.quantity;
  const productivity =
    currentProductivity !== null ? currentProductivity : task.productivity;
  const plannedManhours =
    quantity * productivity || task.totalBudgetedManhours || 0;
  const actualManhours = task.totalConsumedManhours || 0;

  const progressPercentage =
    quantity > 0 ? (task.totalInstalledQuantity / quantity) * 100 : 0;

  if (actualManhours === 0) {
    return {
      cpi: null,
      earnedValue: 0,
      actualCost: 0,
      interpretation: "No Progress",
    };
  }

  if (plannedManhours === 0) {
    return {
      cpi: null,
      earnedValue: 0,
      actualCost: actualManhours,
      interpretation: "No Plan",
    };
  }

  const earnedValue =
    plannedManhours * (Math.min(progressPercentage, 100) / 100);
  const actualCost = actualManhours;

  const earnedManhours = task.totalInstalledQuantity * task.productivity;
  const cpi = earnedManhours/ task.totalConsumedManhours;
  
  // const cpi = earnedValue / actualCost;

  let interpretation;
  let colorScheme;

  if (cpi >= 1.0) {
    interpretation = "Under Budget";
    colorScheme = "green";
  } else if (cpi >= 0.9) {
    interpretation = "Near Budget";
    colorScheme = "yellow";
  } else {
    interpretation = "Over Budget";
    colorScheme = "red";
  }

  return {
    cpi: cpi,
    earnedValue: earnedValue,
    actualCost: actualCost,
    interpretation: interpretation,
    colorScheme: colorScheme,
  };
};

// Memoized Compact CPI Badge Component
const CPIBadge = memo(
  ({ task, currentQuantity = null, currentProductivity = null }) => {
    const cpiData = useMemo(
      () => calculateCPI(task, currentQuantity, currentProductivity),
      [task, currentQuantity, currentProductivity]
    );

    if (cpiData.cpi === null) {
      return (
        <Badge
          colorScheme="gray"
          fontSize="xs"
          px={2}
          py={1}
          borderRadius="md"
          textAlign={"start"}
        >
          N/A
        </Badge>
      );
    }

    const cpiFormatted = cpiData.cpi.toFixed(2);

    return (
      <Badge
        colorScheme={cpiData.colorScheme}
        fontSize="xs"
        px={2}
        py={1}
        borderRadius="md"
      >
        {cpiFormatted}
      </Badge>
    );
  }
);

CPIBadge.displayName = "CPIBadge";

// Memoized Expanded Task Details Component
const TaskDetailsPanel = memo(
  ({
    task,
    isChanged,
    getTaskValue,
    editingTasks,
    changedTasks,
    formatNumber,
    formatCurrency,
  }) => {
    // Get current values (either from editing state or original task)
    const currentQuantity = useMemo(
      () =>
        isChanged ? getTaskValue(task, "quantity") || 0 : task.quantity || 0,
      [isChanged, getTaskValue, task]
    );

    const currentProductivity = useMemo(
      () =>
        isChanged
          ? getTaskValue(task, "productivity") || 0
          : task.productivity || 0,
      [isChanged, getTaskValue, task]
    );

    const currentManhours = useMemo(
      () => currentQuantity * currentProductivity,
      [currentQuantity, currentProductivity]
    );

    const currentBudgetedCost = useMemo(
      () => currentManhours * (task.hourlyRate || 0),
      [currentManhours, task.hourlyRate]
    );

    // Calculate all derived values using current state
    const cpiData = useMemo(
      () => calculateCPI(task, currentQuantity, currentProductivity),
      [task, currentQuantity, currentProductivity]
    );

    const quantityProgress = useMemo(
      () =>
        currentQuantity > 0
          ? ((task.totalInstalledQuantity || 0) / currentQuantity) * 100
          : 0,
      [currentQuantity, task.totalInstalledQuantity]
    );

    const timeProgress = useMemo(
      () =>
        currentManhours > 0
          ? ((task.totalConsumedManhours || 0) / currentManhours) * 100
          : 0,
      [currentManhours, task.totalConsumedManhours]
    );

    const lapsedHours = useMemo(() => {
      const totalEarnedManhours =
        (task.totalInstalledQuantity || 0) * currentProductivity;
      const totalConsumed = task.totalConsumedManhours || 0;
      return totalEarnedManhours - totalConsumed;
    }, [
      task.totalInstalledQuantity,
      task.totalConsumedManhours,
      currentProductivity,
    ]);

    const remainingHours = useMemo(
      () => currentManhours - (task.totalConsumedManhours || 0),
      [currentManhours, task.totalConsumedManhours]
    );

    return (
      <Box
        bg="gray.850"
        border="1px solid"
        borderColor="gray.600"
        borderRadius="md"
        p={4}
        m={2}
      >
        {/* Detailed Stats Grid */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 6 }} spacing={4} mb={4}>
          {/* Installed Quantity */}
          <Card bg="gray.800" size="sm">
            <CardBody>
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">
                  Installed Quantity
                </StatLabel>
                <StatNumber color="green.300" fontSize="lg">
                  {formatNumber(task.totalInstalledQuantity || 0)}
                </StatNumber>
                <StatHelpText color="gray.500" fontSize="xs">
                  <StatArrow
                    type={quantityProgress >= 50 ? "increase" : "decrease"}
                  />
                  {quantityProgress.toFixed(1)}% of{" "}
                  {formatNumber(currentQuantity)} {task.unit}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          {/* Consumed Hours */}
          <Card bg="gray.800" size="sm">
            <CardBody>
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">
                  Consumed Hours
                </StatLabel>
                <StatNumber color="orange.300" fontSize="lg">
                  {formatNumber(task.totalConsumedManhours || 0)}
                </StatNumber>
                <StatHelpText color="gray.500" fontSize="xs">
                  of {formatNumber(currentManhours)} budgeted
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          {/* Earned Hours */}
          {/* Earned Hours */}
          <Card bg="gray.800" size="sm">
            <CardBody>
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">
                  Earned Hours
                </StatLabel>
                <StatNumber color="orange.300" fontSize="lg">
                  {formatNumber(
                    (task.totalInstalledQuantity || 0) * currentProductivity
                  )}
                </StatNumber>
                <StatHelpText color="gray.500" fontSize="xs">
                  {task.totalInstalledQuantity} {task.unit} x{" "}
                  {formatNumber(currentProductivity)} hrs/{task.unit}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          {/* Lapsed Hours */}
          <Card bg="gray.800" size="sm">
            <CardBody>
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">
                  Lapsed Hours
                </StatLabel>
                <StatNumber color="orange.300" fontSize="lg">
                  {formatNumber(lapsedHours || 0)}
                </StatNumber>
                <StatHelpText color="gray.500" fontSize="xs">
                  {formatNumber(
                    currentQuantity - (task.totalInstalledQuantity || 0)
                  )}{" "}
                  {task.unit} left
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          {/* Remaining Hours */}
          <Card bg="gray.800" size="sm">
            <CardBody>
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">
                  Remaining Hours
                </StatLabel>
                <StatNumber color="blue.300" fontSize="lg">
                  {formatNumber(remainingHours || 0)}
                </StatNumber>
                <StatHelpText color="gray.500" fontSize="xs">
                  {timeProgress.toFixed(1)}% used
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          {/* CPI Detail */}
          <Card bg="gray.800" size="sm">
            <CardBody>
              <Stat>
                <StatLabel color="gray.400" fontSize="xs">
                  Cost Performance Index
                </StatLabel>
                <StatNumber
                  color={
                    cpiData.colorScheme === "green"
                      ? "green.300"
                      : cpiData.colorScheme === "yellow"
                        ? "yellow.300"
                        : "red.300"
                  }
                  fontSize="lg"
                >
                  {cpiData.cpi ? cpiData.cpi.toFixed(3) : "N/A"}
                </StatNumber>
                <StatHelpText color="gray.500" fontSize="xs">
                  {cpiData.interpretation}
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Cost Breakdown */}
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} mb={4}>
          <Card bg="gray.800" size="sm">
            <CardHeader pb={2}>
              <Text fontSize="sm" fontWeight="semibold" color="white">
                Budgeted Cost
              </Text>
            </CardHeader>
            <CardBody pt={0}>
              <VStack align="start" spacing={2}>
                <Text color="green.300" fontSize="xl" fontWeight="bold">
                  {formatCurrency(currentBudgetedCost, task.currency)}
                </Text>
                <Text color="gray.400" fontSize="xs">
                  {/* @ {formatCurrency(task.hourlyRate || 0, task.currency)}/hour */}
                </Text>
                <Progress
                  value={100}
                  colorScheme="green"
                  size="sm"
                  borderRadius="md"
                  w="100%"
                />
              </VStack>
            </CardBody>
          </Card>

          <Card bg="gray.800" size="sm">
            <CardHeader pb={2}>
              <Text fontSize="sm" fontWeight="semibold" color="white">
                Consumed Cost
              </Text>
            </CardHeader>
            <CardBody pt={0}>
              <VStack align="start" spacing={2}>
                <Text color="red.300" fontSize="xl" fontWeight="bold">
                  {formatCurrency(task.consumedCost || 0, task.currency)}
                </Text>
                <Text color="gray.400" fontSize="xs">
                  {(
                    ((task.consumedCost || 0) / (currentBudgetedCost || 1)) *
                    100
                  ).toFixed(1)}
                  % of budget
                </Text>
                <Progress
                  value={Math.min(
                    ((task.consumedCost || 0) / (currentBudgetedCost || 1)) *
                    100,
                    100
                  )}
                  colorScheme={
                    (task.consumedCost || 0) / (currentBudgetedCost || 1) <= 0.8
                      ? "green"
                      : (task.consumedCost || 0) / (currentBudgetedCost || 1) <=
                        1
                        ? "yellow"
                        : "red"
                  }
                  size="sm"
                  borderRadius="md"
                  w="100%"
                />
              </VStack>
            </CardBody>
          </Card>

          <Card bg="gray.800" size="sm">
            <CardHeader pb={2}>
              <Text fontSize="sm" fontWeight="semibold" color="white">
                {" "}
                Remaining Cost
              </Text>
            </CardHeader>
            <CardBody pt={0}>
              <VStack align="start" spacing={2}>
                <Text
                  color={
                    currentBudgetedCost - (task.consumedCost || 0) < 0
                      ? "red.400"
                      : currentBudgetedCost - (task.consumedCost || 0) >
                        currentBudgetedCost * 0.8
                        ? "green.400"
                        : "yellow.400"
                  }
                  fontSize="xl"
                  fontWeight="bold"
                >
                  {formatCurrency(
                    currentBudgetedCost - (task.consumedCost || 0) || 0,
                    task.currency
                  )}
                </Text>
                <Text color="gray.400" fontSize="xs">
                  {(
                    ((currentBudgetedCost - (task.consumedCost || 0) || 0) /
                      (currentBudgetedCost || 1)) *
                    100
                  ).toFixed(1)}
                  % remaining
                </Text>
                <Progress
                  value={Math.max(
                    ((currentBudgetedCost - (task.consumedCost || 0) || 0) /
                      (currentBudgetedCost || 1)) *
                    100,
                    0
                  )}
                  colorScheme={
                    currentBudgetedCost - (task.consumedCost || 0) >= 0
                      ? "blue"
                      : "red"
                  }
                  size="sm"
                  borderRadius="md"
                  w="100%"
                />
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Activity Details */}
        <Card bg="gray.800" size="sm">
          <CardHeader pb={2}>
            <HStack>
              <FiInfo color="#9F7AEA" />
              <Text fontSize="sm" fontWeight="semibold" color="white">
                Activity Information
              </Text>
            </HStack>
          </CardHeader>
          <CardBody pt={0}>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <VStack align="start" spacing={1}>
                <Text fontSize="xs" color="gray.400">
                  Activity Code
                </Text>
                <Text
                  fontSize="sm"
                  color="white"
                  fontFamily="mono"
                  fontWeight="bold"
                >
                  {task.masterSubTask?.masterActivity?.code || "N/A"}
                </Text>
              </VStack>
              {/* <VStack align="start" spacing={1}>
                <Text fontSize="xs" color="gray.400">
                  Activity Order
                </Text>
                <Text fontSize="sm" color="white">
                  {task.masterSubTask?.masterActivity?.order || 0}
                </Text>
              </VStack>
              <VStack align="start" spacing={1}>
                <Text fontSize="xs" color="gray.400">
                  Task Order
                </Text>
                <Text fontSize="sm" color="white">
                  {task.masterSubTask?.order || 0}
                </Text>
              </VStack> */}
            </SimpleGrid>
            <Box mt={3}>
              <Text fontSize="xs" color="gray.400" mb={1}>
                Task Description
              </Text>
              <Text fontSize="sm" color="white" lineHeight="1.4">
                {task.masterSubTask?.name || "No description available"}
              </Text>
            </Box>
          </CardBody>
        </Card>
      </Box>
    );
  }
);

TaskDetailsPanel.displayName = "TaskDetailsPanel";

const TaskQuantityManager = ({
  categories,
  projectTasks,
  onTaskUpdate,
  projectId,
}) => {
  const [editingTasks, setEditingTasks] = useState({});
  const [saving, setSaving] = useState(false);
  const [changedTasks, setChangedTasks] = useState(new Set());
  const [isFullWidth, setIsFullWidth] = useState(true);
  const [stickyColumns, setStickyColumns] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const { isOpen: isImportModalOpen, onOpen: onImportModalOpen, onClose: onImportModalClose } = useDisclosure();
  const fileInputRef = useRef(null);

  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, lg: false });

  // Memoized helper functions
  const formatNumber = useCallback((num) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num || 0);
  }, []);

  const formatCurrency = useCallback((amount, currency = "AED") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  }, []);

  const calculateManhours = useCallback((quantity, productivity) => {
    const qty = parseFloat(quantity) || 0;
    const prod = parseFloat(productivity) || 0;
    return qty * prod;
  }, []);

  const getTaskValue = useCallback(
    (task, field) => {
      if (editingTasks[task.id] && editingTasks[task.id][field] !== undefined) {
        return editingTasks[task.id][field];
      }
      return task[field];
    },
    [editingTasks]
  );

  const getCurrentManhours = useCallback(
    (task) => {
      const quantity = getTaskValue(task, "quantity");
      const productivity = getTaskValue(task, "productivity");
      return calculateManhours(quantity, productivity);
    },
    [getTaskValue, calculateManhours]
  );

  const getCurrentBudgetedCost = useCallback(
    (task) => {
      const currentManhours = getCurrentManhours(task);
      const hourlyRate = task.hourlyRate || 0;
      return currentManhours * hourlyRate;
    },
    [getCurrentManhours]
  );

  // Memoized to get current values for calculations
  const getCurrentValues = useCallback(
    (task) => {
      const isChanged = changedTasks.has(task.id);
      const quantity = isChanged
        ? getTaskValue(task, "quantity") || 0
        : task.quantity || 0;
      const productivity = isChanged
        ? getTaskValue(task, "productivity") || 0
        : task.productivity || 0;
      return { quantity, productivity, isChanged };
    },
    [changedTasks, getTaskValue]
  );

  // Optimized toggle function
  const toggleRowExpansion = useCallback((taskId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  }, []);

  // Debounced task edit handler
  const handleTaskEdit = useCallback((taskId, field, value) => {
    setEditingTasks((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [field]: value,
      },
    }));

    // Use functional update to avoid stale state
    setChangedTasks((prev) => new Set([...prev, taskId]));
  }, []);

  // Memoized save function
  const saveTasks = useCallback(async () => {
    if (changedTasks.size === 0) return;

    setSaving(true);
    try {
      const tasksToUpdate = Array.from(changedTasks).map((taskId) => {
        const originalTask = projectTasks.find((t) => t.id === taskId);
        const editedData = editingTasks[taskId];

        return {
          id: taskId,
          quantity: parseFloat(
            editedData.quantity || originalTask.quantity || 0
          ),
          productivity: parseFloat(
            editedData.productivity || originalTask.productivity || 0
          ),
          unit: editedData.unit || originalTask.unit,
          hourlyRate: parseFloat(originalTask.hourlyRate || 0),
        };
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/manpower/tasks/quantities`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ tasks: tasksToUpdate }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Success",
          description: `Updated ${tasksToUpdate.length} tasks successfully`,
          status: "success",
          duration: 3000,
        });

        setEditingTasks({});
        setChangedTasks(new Set());
        onTaskUpdate();
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to update tasks",
          status: "error",
          duration: 4000,
        });
      }
    } catch (error) {
      console.error("Error saving tasks:", error);
      toast({
        title: "Error",
        description: "Failed to save changes",
        status: "error",
        duration: 4000,
      });
    } finally {
      setSaving(false);
    }
  }, [changedTasks, editingTasks, projectTasks, onTaskUpdate, toast]);

  // Memoized reset function
  const resetChanges = useCallback(() => {
    setEditingTasks({});
    setChangedTasks(new Set());
  }, []);

  // Import from Excel
  const handleFileSelect = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // Validate and prepare data
        const validTasks = [];
        const errors = [];

        jsonData.forEach((row, index) => {
          const taskId = row['Task ID'];
          const quantity = parseFloat(row['Quantity']);
          const productivity = parseFloat(row['Productivity']);
          const unit = row['Unit'];

          // Validation
          if (!taskId) {
            errors.push(`Row ${index + 2}: Missing Task ID`);
            return;
          }
          if (isNaN(quantity) || quantity < 0) {
            errors.push(`Row ${index + 2}: Invalid quantity`);
            return;
          }
          if (isNaN(productivity) || productivity <= 0) {
            errors.push(`Row ${index + 2}: Invalid productivity`);
            return;
          }

          validTasks.push({
            id: taskId,
            quantity,
            productivity,
            unit: unit || 'No',
            category: row['Category'],
            activity: row['Activity'],
            subtask: row['SubTask'],
          });
        });

        if (errors.length > 0) {
          toast({
            title: "Validation errors",
            description: errors[0],
            status: "error",
            duration: 5000,
          });
          setImporting(false);
          return;
        }

        setImportPreview(validTasks);
        onImportModalOpen();
      } catch (error) {
        console.error('Import error:', error);
        toast({
          title: "Import failed",
          description: "Failed to parse Excel file",
          status: "error",
          duration: 4000,
        });
      } finally {
        setImporting(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };

    reader.readAsArrayBuffer(file);
  }, [toast, onImportModalOpen]);

  // Confirm import
  const confirmImport = useCallback(async () => {
    if (!importPreview) return;

    setImporting(true);
    try {
      const tasksToUpdate = importPreview.map(task => ({
        id: task.id,
        quantity: task.quantity,
        productivity: task.productivity,
        unit: task.unit,
      }));

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/manpower/tasks/quantities`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ tasks: tasksToUpdate }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        toast({
          title: "Import successful",
          description: `Updated ${tasksToUpdate.length} tasks from Excel`,
          status: "success",
          duration: 4000,
        });

        setImportPreview(null);
        onImportModalClose();
        onTaskUpdate();
      }  else {
        toast({
          title: "Import failed",
          description: result.message || "Failed to update tasks",
          status: "error",
          duration: 4000,
        });
      }
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: "Import failed",
        description: "Failed to update tasks",
        status: "error",
        duration: 4000,
      });
    } finally {
      setImporting(false);
    }
  }, [importPreview, toast, onImportModalClose, onTaskUpdate]);

  // Memoized category processing
  const processedCategories = useMemo(() => {
    if (!categories || categories.length === 0) return [];

    const query = searchQuery?.toLowerCase() || "";

    return [...categories]
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((category) => {
        let sortedTasks = [...category.projectTasks].sort((a, b) => {
          const activityOrderA = a.masterSubTask?.masterActivity?.order || 0;
          const activityOrderB = b.masterSubTask?.masterActivity?.order || 0;
          if (activityOrderA !== activityOrderB) {
            return activityOrderA - activityOrderB;
          }
          const subtaskOrderA = a.masterSubTask?.order || 0;
          const subtaskOrderB = b.masterSubTask?.order || 0;
          return subtaskOrderA - subtaskOrderB;
        });

        // Apply search filter
        if (query) {
          sortedTasks = sortedTasks.filter((task) =>
            task.masterSubTask?.name?.toLowerCase().includes(query) ||
            task.masterSubTask?.code?.toLowerCase().includes(query) ||
            task.masterSubTask?.masterActivity?.name?.toLowerCase().includes(query) ||
            task.masterSubTask?.masterActivity?.code?.toLowerCase().includes(query) ||
            category.name?.toLowerCase().includes(query)
          );
        }

        const categoryTotal = sortedTasks.reduce((sum, task) => {
          const currentValues = getCurrentValues(task);
          return sum + currentValues.quantity * currentValues.productivity;
        }, 0);

        const categoryConsumed = sortedTasks.reduce(
          (sum, task) => sum + (task.totalConsumedManhours || 0),
          0
        );
        const categoryTotalCost = sortedTasks.reduce((sum, task) => {
          const currentValues = getCurrentValues(task);
          const currentManhours =
            currentValues.quantity * currentValues.productivity;
          return sum + currentManhours * (task.hourlyRate || 0);
        }, 0);

        const categoryQuantityProgress =
          sortedTasks.length > 0
            ? sortedTasks.reduce((sum, task) => {
              const currentValues = getCurrentValues(task);
              const taskQuantityProgress =
                currentValues.quantity > 0
                  ? ((task.totalInstalledQuantity || 0) /
                    currentValues.quantity) *
                  100
                  : 0;
              return sum + taskQuantityProgress;
            }, 0) / sortedTasks.length
            : 0;

        return {
          ...category,
          sortedTasks,
          categoryTotal,
          categoryConsumed,
          categoryTotalCost,
          categoryQuantityProgress,
        };
      })
      .filter((category) => category.sortedTasks.length > 0);
  }, [categories, getCurrentValues, searchQuery]);

  // Export to Excel (editable template) - defined after processedCategories
  const exportToExcel = useCallback(() => {
    const data = processedCategories.flatMap(category =>
      category.sortedTasks?.map(task => ({
        'Category': category.name,
        'Activity Code': task.masterSubTask?.masterActivity?.code || '',
        'Activity': task.masterSubTask?.masterActivity?.name || '',
        'SubTask': task.masterSubTask?.name || '',
        'Quantity': task.quantity || 0,
        'Unit': task.unit || '',
        'Productivity': task.productivity || 0,
        'Task ID': task.id, // Hidden column for matching
      })) || []
    );

    const ws = XLSX.utils.json_to_sheet(data);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 20 }, // Category
      { wch: 18 }, // Activity Code
      { wch: 25 }, // Activity
      { wch: 40 }, // SubTask
      { wch: 10 }, // Quantity
      { wch: 8 },  // Unit
      { wch: 12 }, // Productivity
      { wch: 10 }, // Task ID
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Task Data');
    XLSX.writeFile(wb, `${projectId}-task-template.xlsx`);

    toast({
      title: "Template exported",
      description: "Fill in the Quantity, Unit, and Productivity columns, then re-import",
      status: "success",
      duration: 5000,
    });
  }, [processedCategories, projectId, toast]);

  if (!categories || categories.length === 0) {
    return (
      <Alert status="info" borderRadius="md" size="sm">
        <AlertIcon />
        <VStack align="start" spacing={1}>
          <Text fontWeight="semibold" fontSize="sm">
            No categories configured
          </Text>
          <Text fontSize="xs">
            Use the Setup Categories button to initialize your manpower budget.
          </Text>
        </VStack>
      </Alert>
    );
  }

  return (
    <VStack spacing={4} align="stretch" w="100%" maxW="100vw" overflow="hidden">
      {/* Header */}
      <Card bg="gray.800" borderColor="gray.700" borderWidth="1px">
        <CardHeader py={3}>
          <Flex
            direction={{ base: "column", xl: "row" }}
            justify="space-between"
            align="start"
            gap={4}
          >
            <VStack align="start" spacing={1}>
              <Heading size="md" color="white">
                Task Quantity Management
              </Heading>
              <Text color="gray.400" fontSize="sm">
                Project tracking with expandable details - no horizontal
                scrolling needed
              </Text>
            </VStack>

            <VStack spacing={2} align="end">
              <HStack spacing={2} flexWrap="wrap">
                {changedTasks.size > 0 && (
                  <Badge
                    colorScheme="yellow"
                    variant="solid"
                    fontSize="xs"
                    px={2}
                    py={1}
                    borderRadius="md"
                  >
                    {changedTasks.size} changes
                  </Badge>
                )}

                <Button
                  size="sm"
                  variant="outline"
                  onClick={resetChanges}
                  colorScheme="gray"
                  fontSize="xs"
                  px={3}
                >
                  Reset
                </Button>

                <Button
                  leftIcon={<FiSave size={12} />}
                  colorScheme="green"
                  size="sm"
                  onClick={saveTasks}
                  isLoading={saving}
                  isDisabled={changedTasks.size === 0}
                  fontSize="xs"
                  px={3}
                >
                  Save
                </Button>

                <Button
                  leftIcon={<FiDownload size={12} />}
                  variant="outline"
                  size="sm"
                  onClick={exportToExcel}
                  colorScheme="blue"
                  fontSize="xs"
                  px={3}
                >
                  Export Template
                </Button>

                <Button
                  leftIcon={<FiUpload size={12} />}
                  variant="solid"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  colorScheme="green"
                  fontSize="xs"
                  px={3}
                  isLoading={importing}
                >
                  Import Data
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
              </HStack>
            </VStack>
          </Flex>
        </CardHeader>
      </Card>

      {/* Search Bar */}
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none">
          <FiSearch color="gray" />
        </InputLeftElement>
        <Input
          placeholder="Search by name, code, activity, category..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          bg="gray.800"
          color="white"
          borderColor="gray.600"
          _placeholder={{ color: "gray.500" }}
          _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px rgba(66, 153, 225, 0.4)" }}
          borderRadius="md"
        />
      </InputGroup>

      {/* Simplified Responsive Table - NO HORIZONTAL SCROLLING */}
      <Card bg="gray.800" borderColor="gray.700" borderWidth="1px">
        <Accordion allowMultiple defaultIndex={[0]}>
          {processedCategories.map((category) => (
            <AccordionItem key={category.id} border="none">
              <AccordionButton
                py={3}
                px={4}
                bg="gray.700"
                _expanded={{ bg: "gray.600" }}
                _hover={{ bg: "gray.600" }}
                borderRadius="md"
                mb={1}
              >
                <Box flex="1" textAlign="left">
                  <HStack justify="space-between" align="center">
                    <VStack align="start" spacing={0}>
                      <Text fontSize="md" fontWeight="bold" color="white">
                        {category.name}
                      </Text>
                      <Text fontSize="xs" color="gray.400">
                        {category.sortedTasks.length} tasks •{" "}
                        {formatNumber(category.categoryTotal)} hrs •{" "}
                        {formatCurrency(category.categoryTotalCost)}
                      </Text>
                    </VStack>

                    <VStack align="end" spacing={0}>
                      <Text fontSize="sm" fontWeight="bold" color="white">
                        {category.categoryQuantityProgress.toFixed(1)}% Complete
                      </Text>
                      <HStack spacing={2}>
                        <Badge
                          colorScheme={
                            category.categoryQuantityProgress > 80
                              ? "green"
                              : category.categoryQuantityProgress > 50
                                ? "yellow"
                                : "red"
                          }
                          fontSize="xs"
                          px={2}
                          py={1}
                          borderRadius="md"
                        >
                          QTY: {category.categoryQuantityProgress.toFixed(0)}%
                        </Badge>
                        <Badge
                          colorScheme="blue"
                          variant="outline"
                          fontSize="xs"
                          px={2}
                          py={1}
                          borderRadius="md"
                        >
                          {formatNumber(category.categoryConsumed)}/
                          {formatNumber(category.categoryTotal)}
                        </Badge>
                      </HStack>
                    </VStack>
                  </HStack>
                </Box>
                <AccordionIcon color="gray.300" boxSize={4} />
              </AccordionButton>

              <AccordionPanel px={0} pb={2}>
                {category.sortedTasks.length > 0 ? (
                  <Box
                    bg="gray.900"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.700"
                    overflow="hidden"
                    maxW="100%"
                  >
                    {/* RESPONSIVE TABLE - NO HORIZONTAL SCROLL */}
                    <Box overflowX="visible" maxW="100%">
                      <Table variant="simple" size="sm" w="100%">
                        <Thead bg="gray.800">
                          <Tr>
                            {/* Details Toggle */}
                            <Th
                              color="gray.300"
                              fontWeight="bold"
                              fontSize="xs"
                              width="60px"
                              py={2}
                              px={2}
                              textAlign="center"
                            >
                              <Tooltip
                                label="Expand/Collapse Details"
                                fontSize="xs"
                              >
                                <Text>Details</Text>
                              </Tooltip>
                            </Th>

                            {/* Task Description - Flexible width */}
                            <Th
                              color="gray.300"
                              fontWeight="bold"
                              width="200px"
                              fontSize="xs"
                              py={2}
                              px={3}
                            >
                              Activity / Task
                            </Th>

                            {/* Editable Fields - Fixed small widths */}
                            <Th
                              color="gray.300"
                              fontWeight="bold"
                              fontSize="xs"
                              width="50px"
                              py={2}
                              px={2}
                              textAlign="center"
                            >
                              Quantity
                            </Th>

                            <Th
                              color="gray.300"
                              fontWeight="bold"
                              fontSize="xs"
                              width="30px"
                              py={2}
                              px={2}
                              textAlign="center"
                            >
                              Unit
                            </Th>

                            <Th
                              color="gray.300"
                              fontWeight="bold"
                              fontSize="xs"
                              width="100px"
                              py={2}
                              px={2}
                              textAlign="center"
                            >
                              Productivity
                            </Th>
                            {/* Summary columns */}
                            <Th
                              color="gray.300"
                              fontWeight="bold"
                              fontSize="xs"
                              width="100px"
                              py={2}
                              px={2}
                              textAlign="center"
                            >
                              Total ManHours
                            </Th>
                            <Th
                              color="gray.300"
                              fontWeight="bold"
                              fontSize="xs"
                              width="100px"
                              py={2}
                              px={2}
                              textAlign="start"
                            >
                              Consumed ManHours
                            </Th>
                            <Th
                              color="gray.300"
                              fontWeight="bold"
                              fontSize="xs"
                              width="100px"
                              py={2}
                              px={2}
                              textAlign="start"
                            >
                              Earned ManHours
                            </Th>

                            <Th
                              color="gray.300"
                              fontWeight="bold"
                              fontSize="xs"
                              width="80px"
                              py={2}
                              px={2}
                              textAlign="start"
                            >
                              CPI
                            </Th>

                            <Th
                              color="gray.300"
                              fontWeight="bold"
                              fontSize="xs"
                              width="80px"
                              py={2}
                              px={2}
                              textAlign="center"
                            >
                              Progress
                            </Th>
                          </Tr>
                        </Thead>

                        <Tbody>
                          {category.sortedTasks
                            .sort((a, b) => {
                              const orderA = a.masterSubTask?.order || 0;
                              const orderB = b.masterSubTask?.order || 0;
                              if (orderA !== orderB) {
                                return orderA - orderB;
                              }
                              if (a.createdAt && b.createdAt) {
                                return (
                                  new Date(a.createdAt) - new Date(b.createdAt)
                                );
                              }
                              return (a.id || 0) - (b.id || 0);
                            })
                            .flatMap((task, index) => {
                              const currentValues = getCurrentValues(task);

                              const isChanged = currentValues.isChanged;
                              const currentManhours =
                                currentValues.quantity *
                                currentValues.productivity;
                              const currentEarnedManhours =
                                (task.totalInstalledQuantity || 0) *
                                currentValues.productivity;

                              const quantityProgress =
                                currentValues.quantity > 0
                                  ? ((task.totalInstalledQuantity || 0) /
                                    currentValues.quantity) *
                                  100
                                  : 0;
                              const isExpanded = expandedRows.has(task.id);

                              const mainRow = (
                                <Tr
                                  key={task.id}
                                  bg={
                                    isChanged
                                      ? "blue.900"
                                      : index % 2 === 0
                                        ? "gray.800"
                                        : "gray.750"
                                  }
                                  borderWidth={isChanged ? "1px" : "0"}
                                  borderColor={
                                    isChanged ? "blue.400" : "transparent"
                                  }
                                  _hover={{
                                    bg: isChanged ? "blue.800" : "gray.700",
                                  }}
                                  transition="all 0.2s"
                                >
                                  {/* Expand/Collapse Button */}
                                  <Td
                                    width="60px"
                                    py={2}
                                    px={2}
                                    textAlign="start"
                                  >
                                    <IconButton
                                      size="xs"
                                      variant="ghost"
                                      colorScheme="blue"
                                      icon={
                                        isExpanded ? (
                                          <FiMinus size={12} />
                                        ) : (
                                          <FiPlus size={12} />
                                        )
                                      }
                                      onClick={() =>
                                        toggleRowExpansion(task.id)
                                      }
                                      aria-label={
                                        isExpanded
                                          ? "Collapse details"
                                          : "Expand details"
                                      }
                                      _hover={{
                                        bg: "blue.600",
                                        color: "white",
                                      }}
                                    />
                                  </Td>

                                  {/* Task Description - Flexible */}
                                  <Td py={2} px={3} maxW="0" overflow="hidden">
                                    <VStack align="start" spacing={1}>
                                      <Text
                                        fontSize="xs"
                                        color="gray.400"
                                        fontFamily="mono"
                                        fontWeight="bold"
                                      >
                                        {
                                          task.masterSubTask?.masterActivity
                                            ?.code
                                        }
                                      </Text>
                                      <Text
                                        fontSize="xs"
                                        fontWeight="medium"
                                        color="white"
                                        lineHeight="1.2"
                                        noOfLines={2}
                                      >
                                        {task.masterSubTask?.name}
                                      </Text>
                                    </VStack>
                                  </Td>

                                  {/* Quantity Input */}
                                  <Td width="100px" py={2} px={2}>
                                    <NumberInput
                                      size="sm"
                                      min={0}
                                      precision={3}
                                      value={getTaskValue(task, "quantity")}
                                      onChange={(value) =>
                                        handleTaskEdit(
                                          task.id,
                                          "quantity",
                                          value
                                        )
                                      }
                                    >
                                      <NumberInputField
                                        bg="gray.700"
                                        color="white"
                                        border="1px solid"
                                        borderColor={
                                          isChanged ? "blue.400" : "gray.600"
                                        }
                                        borderRadius="md"
                                        _focus={{
                                          borderColor: "blue.400",
                                          boxShadow:
                                            "0 0 0 2px rgba(66, 153, 225, 0.2)",
                                        }}
                                        _hover={{ borderColor: "blue.500" }}
                                        fontSize="xs"
                                        py={2}
                                        px={2}
                                      />
                                    </NumberInput>
                                  </Td>

                                  {/* Unit read only */}
                                  <Td width="80px" py={2} px={2}>
                                    <Input
                                      size="sm"
                                      value={getTaskValue(task, "unit")}
                                      isReadOnly
                                      bg="gray.700"
                                      color="white"
                                      border="1px solid"
                                      borderColor={
                                        isChanged ? "blue.400" : "gray.600"
                                      }
                                      borderRadius="md"
                                      _focus={{
                                        borderColor: "gray.600",
                                      }}
                                      _hover={{ borderColor: "gray.600" }}
                                      fontSize="xs"
                                      py={2}
                                      px={2}
                                      cursor="not-allowed"
                                      opacity={0.8}
                                    />
                                  </Td>

                                  {/* Productivity Input read only */}
                                  <Td width="100px" py={2} px={2}>
                                    <NumberInput
                                      size="sm"
                                      min={0}
                                      precision={2}
                                      step={0.1}
                                      value={getTaskValue(task, "productivity")}
                                      isReadOnly
                                    >
                                      <NumberInputField
                                        bg="gray.700"
                                        color="white"
                                        border="1px solid"
                                        borderColor={
                                          isChanged ? "blue.400" : "gray.600"
                                        }
                                        borderRadius="md"
                                        _focus={{
                                          borderColor: "gray.600",
                                        }}
                                        _hover={{ borderColor: "gray.600" }}
                                        fontSize="xs"
                                        py={2}
                                        px={2}
                                        cursor="not-allowed"
                                        opacity={0.8}
                                      />
                                    </NumberInput>
                                  </Td>

                                  {/* Hours Summary */}
                                  <Td width="100px" isNumeric py={2} px={2}>
                                    <Text
                                      color={
                                        isChanged ? "blue.300" : "gray.200"
                                      }
                                      fontWeight={isChanged ? "bold" : "normal"}
                                      fontSize="xs"
                                      textAlign={"start"}
                                    >
                                      {formatNumber(currentManhours)}
                                    </Text>
                                  </Td>
                                  {/* Consumed ManHours */}
                                  <Td width="100px" isNumeric py={2} px={2}>
                                    <Text
                                      color={
                                        isChanged ? "blue.300" : "gray.200"
                                      }
                                      fontWeight={isChanged ? "bold" : "normal"}
                                      fontSize="xs"
                                      textAlign={"start"}
                                    >
                                      {formatNumber(task.totalConsumedManhours)}
                                    </Text>
                                  </Td>
                                  {/* Earned ManHours */}
                                  <Td width="100px" isNumeric py={2} px={2}>
                                    <Text
                                      color={
                                        isChanged ? "blue.300" : "gray.200"
                                      }
                                      fontWeight={isChanged ? "bold" : "normal"}
                                      fontSize="xs"
                                      textAlign={"start"}
                                    >
                                      {formatNumber(currentEarnedManhours)}
                                    </Text>
                                  </Td>

                                  {/* CPI */}
                                  <Td
                                    width="80px"
                                    isNumeric
                                    py={2}
                                    px={2}
                                    textAlign={"start !important"}
                                  >
                                    <CPIBadge
                                      task={task}
                                      currentQuantity={currentValues.quantity}
                                      currentProductivity={
                                        currentValues.productivity
                                      }
                                    />
                                  </Td>

                                  {/* Progress */}
                                  <Td width="80px" py={2} px={2}>
                                    <VStack spacing={1} align="center">
                                      <Badge
                                        colorScheme={
                                          quantityProgress >= 100
                                            ? "green"
                                            : quantityProgress >= 75
                                              ? "yellow"
                                              : quantityProgress > 0
                                                ? "blue"
                                                : "gray"
                                        }
                                        fontSize="xs"
                                        px={1}
                                        py={1}
                                        borderRadius="md"
                                      >
                                        {quantityProgress.toFixed(0)}%
                                      </Badge>
                                    </VStack>
                                  </Td>
                                </Tr>
                              );

                              // Expanded row with all details
                              const expandedRow = isExpanded ? (
                                <Tr key={`${task.id}-expanded`}>
                                  <Td colSpan="12" p={0} bg="gray.850">
                                    <TaskDetailsPanel
                                      task={task}
                                      isChanged={isChanged}
                                      getTaskValue={getTaskValue}
                                      editingTasks={editingTasks}
                                      changedTasks={changedTasks}
                                      formatNumber={formatNumber}
                                      formatCurrency={formatCurrency}
                                    />
                                  </Td>
                                </Tr>
                              ) : null;

                              return expandedRow
                                ? [mainRow, expandedRow]
                                : [mainRow];
                            })}
                        </Tbody>
                      </Table>
                      {/* Category Summary Row */}
                      <Box
                        bg="gray.700"
                        borderTop="2px solid"
                        borderColor="blue.500"
                        p={3}
                        mt={2}
                      >
                        <Flex justify="space-between" align="center">
                          <Text fontSize="sm" fontWeight="bold" color="white">
                            Category Total
                          </Text>
                          <HStack spacing={6}>
                            <VStack align="end" spacing={0}>
                              <Text fontSize="xs" color="gray.400">
                                Total Manhours
                              </Text>
                              <Text
                                fontSize="md"
                                fontWeight="bold"
                                color="blue.300"
                              >
                                {formatNumber(category.categoryTotal)}
                              </Text>
                            </VStack>
                            <VStack align="end" spacing={0}>
                              <Text fontSize="xs" color="gray.400">
                                Earned Manhours
                              </Text>
                              <Text
                                fontSize="md"
                                fontWeight="bold"
                                color="green.300"
                              >
                                {formatNumber(
                                  category.sortedTasks.reduce((sum, task) => {
                                    return sum + (task.totalInstalledQuantity || 0) * (getCurrentValues(task).productivity || 0);
                                  }, 0)
                                )}
                              </Text>
                            </VStack>
                            <VStack align="end" spacing={0}>
                              <Text fontSize="xs" color="gray.400">
                                Consumed Manhours
                              </Text>
                              <Text
                                fontSize="md"
                                fontWeight="bold"
                                color="orange.300"
                              >
                                {formatNumber(category.categoryConsumed)}
                              </Text>
                            </VStack>
                            <VStack align="end" spacing={0}>
                              <Text fontSize="xs" color="gray.400">
                                Remaining Manhours
                              </Text>
                              <Text
                                fontSize="md"
                                fontWeight="bold"
                                color="green.300"
                              >
                                {formatNumber(
                                  category.categoryTotal -
                                  category.categoryConsumed
                                )}
                              </Text>
                            </VStack>
                          </HStack>
                        </Flex>
                      </Box>

                    </Box>
                  </Box>
                ) : (
                  <Card bg="gray.800">
                    <CardBody py={4}>
                      <Text color="gray.300" textAlign="center" fontSize="sm">
                        No tasks found for this category
                      </Text>
                    </CardBody>
                  </Card>
                )}
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Card>

      {/* Import Preview Modal */}
      <Modal isOpen={isImportModalOpen} onClose={onImportModalClose} size="xl">
        <ModalOverlay />
        <ModalContent bg="gray.800" borderColor="gray.700">
          <ModalHeader color="white">Import Preview</ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            <VStack align="start" spacing={4}>
              <Alert status="info" bg="blue.900" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>Ready to import {importPreview?.length || 0} tasks</AlertTitle>
                  <AlertDescription fontSize="sm">
                    Review the changes below and click "Confirm Import" to update all tasks.
                  </AlertDescription>
                </Box>
              </Alert>

              <Box maxH="400px" overflowY="auto" w="full">
                <Table size="sm" variant="simple">
                  <Thead bg="gray.700" position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th color="gray.300">Category</Th>
                      <Th color="gray.300">SubTask</Th>
                      <Th color="gray.300" isNumeric>Quantity</Th>
                      <Th color="gray.300">Unit</Th>
                      <Th color="gray.300" isNumeric>Productivity</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {importPreview?.map((task, index) => (
                      <Tr key={index}>
                        <Td color="gray.300" fontSize="xs">{task.category}</Td>
                        <Td color="gray.300" fontSize="xs">{task.subtask}</Td>
                        <Td color="white" fontSize="xs" isNumeric fontWeight="bold">{task.quantity}</Td>
                        <Td color="gray.300" fontSize="xs">{task.unit}</Td>
                        <Td color="white" fontSize="xs" isNumeric fontWeight="bold">{task.productivity}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              <Alert status="success" bg="green.900" borderRadius="md">
                <AlertIcon />
                <Text color="white" fontSize="sm">
                  All validations passed. Safe to import.
                </Text>
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={onImportModalClose}>
                Cancel
              </Button>
              <Button
                colorScheme="green"
                onClick={confirmImport}
                isLoading={importing}
                loadingText="Importing..."
              >
                Confirm Import
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default TaskQuantityManager;
